import React, { useState } from "react";
import "./index.css";

interface InputProps {
  id?: any;
  label: string;
  errors: any;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  name: string;
  onClick?: () => void;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  required?: boolean;
  defaultValue?: any;
  value?: any;
  className?: string;
  maxLength?: number;
  register: any;
  multiline?: boolean;
  rows?: number;
  readonly?: boolean;
  uppercase?: boolean;
}

// Helper to safely access nested error values including array notation
function getNestedValue(obj: any, path: string): any {
  // Handle array notation like executiveDetails[0].firstName
  const normalizedPath = path
    .replace(/\[(\d+)\]/g, '.$1') // Convert [0] to .0
    .replace(/^\./, ''); // Remove leading dot if any

  return normalizedPath.split('.').reduce((acc, part) => {
    return acc && acc[part] !== undefined ? acc[part] : undefined;
  }, obj);
}

const Input: React.FC<InputProps> = ({
  label,
  errors,
  type = "text",
  placeholder,
  autoComplete,
  name,
  disabled = false,
  onChange,
  defaultValue,
  onBlur,
  required = true,
  className = "",
  maxLength,
  value,
  readonly,
  register,
  multiline = false,
  rows = 3,
  uppercase = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let newValue = e.target.value;

    if (type === "number" || type === "tel") {
      newValue = newValue.replace(/\D/g, "");
      if (maxLength) newValue = newValue.slice(0, maxLength);
    }

    if (uppercase && type !== "password" && type !== "number" && type !== "tel") {
      newValue = newValue.toUpperCase();
    }

    if (onChange) onChange(newValue);
    e.target.value = newValue;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      if (!/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  const registerWithHandlers = {
    ...register(name),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      register(name).onChange(e);
      handleInputChange(e);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      register(name).onBlur(e);
      if (onBlur) onBlur(e);
    },
  };

  const processedDefaultValue =
    uppercase && defaultValue && typeof defaultValue === "string"
      ? defaultValue.toUpperCase()
      : defaultValue;

  const processedValue =
    uppercase && value && typeof value === "string" ? value.toUpperCase() : value;

  const errorMessage = getNestedValue(errors, name)?.message;

  return (
    <div className={`input-container ${className}`}>
      {label ? <label htmlFor={name} className="input-label">{label}</label> : <label className="input-label"></label>}

      <div className="relative">
        {multiline ? (
          <textarea
            id={name}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            rows={rows}
            defaultValue={processedDefaultValue}
            value={processedValue}
            className={`placeholder-gray-400 font-medium input-field ${errorMessage ? "input-error" : ""}`}
            {...registerWithHandlers}
          />
        ) : (
          <input
            id={name}
            name={name}
            type={type === "password" && showPassword ? "text" : type}
            placeholder={placeholder}
            autoComplete={autoComplete}
            disabled={disabled}
            defaultValue={processedDefaultValue}
            readOnly={readonly}
            value={processedValue}
            maxLength={type !== "number" ? maxLength : undefined}
            inputMode={type === "number" ? "numeric" : undefined}
            onKeyDown={handleKeyDown}
            className={`placeholder-gray-400 font-medium input-field ${errorMessage ? "input-error" : ""}`}
            {...registerWithHandlers}
          />
        )}

        {type === "password" && !multiline && (
          <button type="button" onClick={togglePasswordVisibility} className="password-toggle">
            {showPassword ? (
              <img src="/path-to-eye-closed-icon" alt="Hide Password" />
            ) : (
              <img src="/path-to-eye-opened-icon" alt="Show Password" />
            )}
          </button>
        )}
      </div>

      {errorMessage && <p className="input-error-message">{errorMessage}</p>}
    </div>
  );
};

export default Input;
